import { Request } from "@/types/Request";
import * as XLSX from 'xlsx-js-style';
import moment from 'moment';
import { getRequestTypeValue, getPaymentTypeValue, getPaymentCycleTypeValue, getDocumentTypeValue, getRequestStatusTypeValue } from "@/utils/getValueForKey";
import { formatCurrency, getBeusPlanLName, getBaPlanLName } from "@/utils";

const convertDataForExcel = (data: Request[]): Record<string, any>[] => {
  return data.map((row) => ({
    '번호': row.requestId,
    '뷰저블 계정': row.userId,
    '회사명': row.companyName,
    '담당자 이메일': row.manager ? row.manager.map(ma => ma.mail).join(', ') : '',
    '상태': row.state.map(st => getRequestStatusTypeValue(st)).join(', '),
    // '담당자 연락처': row.userTel,
    '요청 유형': getRequestTypeValue(row.requestType),
    '히트맵 플랜': row.beusPlanNumber ? getBeusPlanLName(row.beusPlanNumber) : '',
    '저니맵 플랜': row.baPlanNumber ? getBaPlanLName(row.baPlanNumber) : '',
    '사용 시작일': row.useStartDate,
    '사용 종료일': row.useEndDate,
    '사용 기간(개월)': row.requestType === 'ADDITIONAL' ? '-' : `${row.period}개월`,
    '자동 갱신 여부': row.recurring ? 'Yes' : 'No',
    '결제 방식': getPaymentTypeValue(row.paymentType),
    '총 결제 금액': formatCurrency(row.amount),
    '최초 결제일': row.paymentDate,
    '결제 주기': row.paymentCycle ? getPaymentCycleTypeValue(row.paymentCycle) : getPaymentCycleTypeValue(0),
    '발행 서류': row.docList ? row.docList.map(doc => getDocumentTypeValue(doc)).join(", ") : '',
    '메모': row.memo,
  }));
};

const applyStyles = (worksheet: XLSX.WorkSheet) => {
  const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '6941C6' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };

  const dataStyle = {
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  };
  
  const range = XLSX.utils.decode_range(worksheet['!ref']!);
  
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!worksheet[cellAddress]) continue;
    worksheet[cellAddress].s = headerStyle;
  }

  for (let R = 1; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = dataStyle;
    }
  }
};

export const createExcel = (data: Request[]) => {
  const today = moment().format('YYYY-MM-DD'); 
  const excelData = convertDataForExcel(data);

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const range = XLSX.utils.decode_range(worksheet['!ref']!);
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
  worksheet['!cols'] = [
    { width: 27 }, // 번호
    { width: 40 }, // 뷰저블 계정
    { width: 40 }, // 회사명
    { width: 40 }, // 담당자 이메일
    { width: 40 }, // 상태
    { width: 14 }, // 요청 유형
    { width: 18 }, // 히트맵 플랜
    { width: 18 }, // 저니맵 플랜
    { width: 18 }, // 사용 시작일
    { width: 18 }, // 사용 종료일
    { width: 14 }, // 사용 기간 (개월))
    { width: 14 }, // 자동 갱신 여부
    { width: 14 }, // 결제 방식
    { width: 14 }, // 총 결제 금액
    { width: 18 }, // 최초 결제일
    { width: 18 }, // 결제 주기
    { width: 60 }, // 발행 서류
    { width: 60 } // 메모
  ];
  
  const headerRowHeight = [{ hpt: 40 }];
  const dataRowHeight = Array.from({ length: data.length }, () => ({ hpt: 30 }))

  worksheet['!rows'] = [...headerRowHeight, ...dataRowHeight];

  applyStyles(worksheet);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `${today}`);
  XLSX.writeFile(workbook, `eagle-m-data-${today}.xlsx`);
};